<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import {
  UiDialog,
  UiButton,
  UiCheckbox,
  UiFieldContainer,
  UiIcon,
  UiInfoBox,
  UiText,
} from '@shopexperts/shared';
import type { ListingType } from '@shopexperts/legacy-api';
import {
  useExpertSlugUpdate,
  extractSlugErrorMessage,
} from '@/app/composables/expert-manager/useExpertSlugUpdate';
import {
  PUBLIC_SITE_BASE_URL,
  expertProfileBasePath,
  expertProfilePath,
  finalizeSlug,
  normalizeSlugInput,
  validateExpertSlug,
} from './expertSlug';

const props = defineProps<{
  expertId: number;
  expertName: string;
  listingType: ListingType;
  currentSlug: string | null;
}>();

const open = defineModel<boolean>({ default: false });

const { updateSlug } = useExpertSlugUpdate();
const isPending = computed(() => updateSlug.isPending.value);

const slugInput = ref('');
const createRedirect = ref(true);
const touched = ref(false);
const apiError = ref<string | null>(null);
const inputRef = useTemplateRef<HTMLInputElement>('slugField');

/** The prefix is fixed by the expert's listing type — only the last segment is editable. */
const urlPrefix = computed(
  () => `${PUBLIC_SITE_BASE_URL.replace(/^https?:\/\//, '')}${expertProfileBasePath(props.listingType)}/`,
);

const nextSlug = computed(() => finalizeSlug(slugInput.value));
const validationError = computed(() => validateExpertSlug(nextSlug.value, props.currentSlug));
const fieldError = computed(() => apiError.value ?? (touched.value ? validationError.value : null));

const currentPath = computed(() =>
  props.currentSlug ? expertProfilePath(props.listingType, props.currentSlug) : null,
);
const nextPath = computed(() =>
  nextSlug.value ? expertProfilePath(props.listingType, nextSlug.value) : null,
);
const showPreview = computed(() => !!currentPath.value && !!nextPath.value && !validationError.value);

watch(open, (isOpen) => {
  if (!isOpen) return;
  slugInput.value = props.currentSlug ?? '';
  // A redirect only makes sense when there is an old URL that people may have linked to.
  createRedirect.value = !!props.currentSlug;
  touched.value = false;
  apiError.value = null;
  nextTick(() => inputRef.value?.focus());
});

function handleInput(event: Event) {
  slugInput.value = normalizeSlugInput((event.target as HTMLInputElement).value);
  touched.value = true;
  apiError.value = null;
}

async function submit() {
  touched.value = true;
  apiError.value = null;
  if (validationError.value) return;

  try {
    await updateSlug.mutateAsync({
      expertId: props.expertId,
      slug: nextSlug.value,
      createRedirect: createRedirect.value && !!props.currentSlug,
      previousSlug: props.currentSlug,
    });
    open.value = false;
  } catch (error) {
    // Keep the dialog open — every failure here is something the admin can fix.
    apiError.value = extractSlugErrorMessage(error);
  }
}

function cancel() {
  open.value = false;
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Change profile URL"
    size="lg"
  >
    <div class="flex flex-col gap-5">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        :text="`This is the public address of ${expertName}'s profile. Search engines, partner listings and shared links all point at it.`"
      />

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Profile URL" />
        <UiFieldContainer
          :invalid="!!fieldError"
          :error="fieldError ?? undefined"
          :hint="fieldError ? undefined : 'Lowercase letters, numbers and hyphens.'"
          describedByIdBase="expert-slug"
          @focus="inputRef?.focus()"
        >
          <div class="flex min-w-0 flex-1 items-center gap-1.5">
            <UiIcon name="globe-01" class="h-5 w-5 shrink-0 text-se-gray-500" />
            <span class="shrink-0 font-mono text-sm text-se-gray-500">{{ urlPrefix }}</span>
            <input
              id="expert-slug"
              ref="slugField"
              :value="slugInput"
              :disabled="isPending"
              type="text"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              placeholder="expert-name"
              class="min-w-0 flex-1 font-mono text-sm text-se-dark"
              :aria-invalid="fieldError ? 'true' : undefined"
              @input="handleInput"
              @keyup.enter="submit"
            />
          </div>
        </UiFieldContainer>
      </div>

      <template v-if="currentPath">
        <UiCheckbox
          v-model="createRedirect"
          label="Redirect the old URL to the new one"
          description="Adds a permanent (301) redirect so existing links and search results keep working."
          :disabled="isPending"
        />

        <!-- What the change actually does to the two paths. -->
        <div
          v-if="showPreview"
          class="flex flex-col gap-2 rounded-xl border border-se-gray-200 bg-se-gray-50 px-4 py-3"
        >
          <div class="flex items-baseline gap-3">
            <span class="w-8 shrink-0 text-[10px] uppercase tracking-wide text-se-gray-500">Old</span>
            <span class="truncate font-mono text-xs text-se-gray-500 line-through">{{ currentPath }}</span>
          </div>
          <div class="flex items-baseline gap-3">
            <span class="w-8 shrink-0 text-[10px] uppercase tracking-wide text-se-gray-500">New</span>
            <span class="truncate font-mono text-xs font-medium text-se-dark">{{ nextPath }}</span>
          </div>
          <div class="flex items-center gap-1.5 border-t border-se-gray-200 pt-2">
            <UiIcon
              :name="createRedirect ? 'arrow-right' : 'info-circle'"
              class="h-3.5 w-3.5 shrink-0"
              :class="createRedirect ? 'text-se-gray-500' : 'text-amber-500'"
            />
            <UiText
              as="span"
              variant="caption-normal"
              :class="createRedirect ? 'text-se-gray-600' : 'text-amber-700'"
              :text="createRedirect
                ? 'The old path will 301 to the new one.'
                : 'The old path will return 404.'"
            />
          </div>
        </div>

        <UiInfoBox v-if="!createRedirect" variant="warning">
          <UiText as="span" variant="p-small">
            Without a redirect, every link and search result pointing at the old URL breaks. Only
            skip it when the old URL was never public.
          </UiText>
        </UiInfoBox>
      </template>

      <UiInfoBox v-else variant="info">
        <UiText as="span" variant="p-small">
          This expert has no public URL yet, so there is nothing to redirect.
        </UiText>
      </UiInfoBox>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="isPending" @click="cancel" />
        <UiButton
          variant="primary"
          :label="isPending ? 'Changing…' : 'Change URL'"
          :disabled="isPending || (touched && !!validationError)"
          @click="submit"
        />
      </div>
    </template>
  </UiDialog>
</template>
